(ns dre.components.beeswarm-plot
  (:require
   ["d3" :as d3]
   ["moment" :as moment]
   ["react-sizeme" :as react-sizeme]
   [applied-science.js-interop :as j]
   [clojure.string :as str]
   [dre.coll :refer [distinct-by]]
   [dre.components.icon :refer [fa-icon]]
   [dre.shared.interface.names :refer [categories]]
   [goog.dom :as gdom]
   [goog.object :as gobject]
   [goog.string :as gstring]
   [reagent.core :as r]
   [reagent.dom.server :as rds]))

(def component-name :beeswarm-plot)

(defn init-data!
  "Clears properties added as side effect of previous d3 run. Sets id
  and fixed value for main icon."
  [data height]
  (doseq [d data]
    (doseq [prop ["x" "y" "vx" "vy"
                  "id" "fx" "fy" "index"]]
      (gobject/remove d prop))
    (aset d "id" (:target/guid d))
    (when (= :thumbtack (:beeswarm/icon d))
      (aset d "fy" (/ height 2)))))

(defn pad-year
  "Returns JS array of unix ms a b padded to minimally a year"
  [[a b]]
  (let [moment-a (-> (moment a)
                     (.startOf "month"))
        moment-b (-> (moment b)
                     (.endOf "month"))
        year-a (.year moment-a)
        year-b (.year moment-b)
        month-a (.month moment-a)
        month-b (.month moment-b)
        day-a (.date moment-a)
        day-b (.date moment-b)
        month-diff (- (+ (* 12 year-b) month-b)
                      (+ (* 12 year-a) month-a))]
    (if (< month-diff 11)
      (let [month-pad (/ (- 11 month-diff) 2)
            [floor ceil] [(js/Math.floor month-pad)
                          (js/Math.ceil month-pad)]
            [left-pad right-pad] (if (< day-a day-b)
                                   [floor ceil] [ceil floor])
            new-a (-> moment-a (.subtract left-pad "months") .valueOf)
            new-b (-> moment-b (.add right-pad "months") .valueOf)]
        #js [new-a new-b])
      #js [a b])))

(defn draw-legend!
  [svg legend-data]
  ;; drawing the legend, inspired by
  ;; http://zeroviscosity.com/d3-js-step-by-step/step-3-adding-a-legend
  (let [rect-height 10
        rect-width rect-height
        legend
        (-> svg
            (.selectAll ".legend")
            (.data legend-data)
            (.enter)
            (.append "g")
            (.attr "class" "legend")
            (.attr "transform"
                   #(do (gstring/format "translate(15,%s)"
                                        (+ 15 (* %2 (+ rect-height 2)))))))]

    (-> legend
        (.append "rect")
        (.attr "width" rect-width)
        (.attr "height" rect-height)
        (.attr "class" #(:beeswarm/class %)))

    (-> legend
        (.append "text")
        (.text #(get categories
                     (:beeswarm/label %)
                     (:beeswarm/label %)))
        (.attr "x" (+ rect-width 2))
        (.attr "y" (- rect-height 2)))))

(defn coordinates-map
  [data]
  (into {}
        (map
         (fn [d]
           (let [icon? (:beeswarm/icon d)
                 x-offset (if icon? 6 0)
                 y-offset (if icon? 2 0)]
             [(:target/guid d)
              {:x (+ (.-x d) x-offset)
               :y (+ (.-y d) y-offset)}])))
        data))


(defn draw-lines!
  [g data]
  (let [guid->coords (coordinates-map data)
        tuples (distinct-by
                (fn [[guid-from guid-to]]
                  #{guid-from guid-to})
                (for [d data
                      n (:target/neighbours d)]
                  [(:target/guid d) (:target/guid n)]))]
    (doseq [[guid-from guid-to] tuples]
      (let [{x-from :x y-from :y} (get guid->coords guid-from)
            {x-to :x y-to :y} (get guid->coords guid-to)]
        (-> g
            (.append "line")
            (.attr "class" "beeswarm-connection")
            (.attr "class"
                   #(str/join " "
                              ["beeswarm-connection"
                               guid-from guid-to]))
            (.attr "from" guid-from)
            (.attr "to" guid-to)
            (.attr "x1" x-from)
            (.attr "x2" x-to)
            (.attr "y1" y-from)
            (.attr "y2" y-to))))))

(defn draw-dots!
  [g data click-handler label-fn]
  (doseq [d data]
    (let [class (str/join " "
                          [(:beeswarm/class d)
                           (:target/guid d)])
          x (.-x d)
          y (.-y d)]
      (-> (if-let [icon (:beeswarm/icon d)]
            (let [a (-> g
                        (.append "foreignObject")
                        (.attr "x" x)
                        (.attr "y" (- y 26))
                        (.attr "width" 20)
                        (.attr "height" 40)
                        (.on "click" (fn [e] (click-handler d))))
                  _ (-> a
                        (.append "xhtml:div")
                        (.attr "class" "icon")
                        (.html (rds/render-to-string [fa-icon icon])))]
              a)
            (-> g
                (.append "circle")
                (.attr "r" 3)
                (.attr "cx" x)
                (.attr "class" class)
                (.attr "id" (:target/guid d))
                (.attr "cy" y)
                (.on "click" (fn [e] (click-handler d)))))
          (.on "mouseover" (fn [e]
                             (let [lines (.selectAll d3 "line")]
                               (doseq [line lines]
                                 (let [from (j/call line :getAttribute "from")
                                       to (j/call line :getAttribute "to")]
                                   (when (or (= (:target/guid d) from) (= (:target/guid d) to))
                                     (.setAttribute line "active" true)))))))
          (.on "mouseout" (fn [e]
                            (let [lines (.selectAll d3 "line")]
                              (doseq [line lines]
                                (.setAttribute line "active" false)))))
          (.append "title")
          (.text (label-fn d))))))

(defn simulate! [data x height top-margin force-collide value-fn]
  (doseq [d data]
    (aset d "fx" (x (value-fn d))))
  (let [sim (-> (.forceSimulation d3 data)
                (.force "x" (-> (.forceX d3 #(let [v (value-fn %)]
                                               (if v (x v)
                                                   (.-x %))))
                                (.strength 1)))
                (.force "y" (.forceY d3 (/ height 2)))
                (.force "collide" (.forceCollide d3 force-collide))
                (.stop))]
    (dotimes [i 120]
      (.tick sim))
    (let [max-points (int (/ (- height top-margin)
                             (* 2 force-collide)))]
      (doseq [[_ g] (group-by (fn [d]
                                (.-x d))
                              data)]
        (when (> (count g) max-points)
          (let [min-y (apply min (map #(.-y %) g))
                max-y (apply max (map #(.-y %) g))
                span (- max-y min-y)
                project-fn (fn [y]
                             (* (- y min-y)
                                (/ (- height top-margin) span)))]
            (doseq [d g]
              (set! (.-y d) (project-fn (.-y d))))))))
    sim))

(defn draw!
  [{:keys [dom-node data
           legend-data
           label-fn 
           value-fn
           click-handler 
           show-lines?] :as args}
   width
   height]
  (let [top-margin 10
        right-margin 30
        bottom-margin 50
        left-margin 30
        d3-el (j/call d3 :select dom-node)
        svg (.. (j/call d3-el :append "svg")
                (attr "xmlns" "http://www.w3.org/2000/svg"))
        width (- width
                 left-margin
                 right-margin)
        height (- height
                  top-margin
                  bottom-margin)
        force-collide (if-not show-lines? 6 10)
        _ (init-data! data height)
        data (sort-by value-fn data)
        legend-data (to-array legend-data)
        data (to-array data)
        x-extent (-> (.extent d3 data (fn [e] (value-fn e)))
                     pad-year)
        x-scale (.. (.scaleTime d3)
                    (range (into-array [0 width]))
                    (domain x-extent))
        x-axis (.. (j/call d3 :axisBottom x-scale)
                   (tickSizeOuter 0))
        g (.. svg
              (append "g")
              (attr "transform" (str "translate(" left-margin "," right-margin ")")))]
    (simulate! data x-scale height top-margin force-collide value-fn)
    (draw-legend! svg legend-data)
    (.. g
        (append "g")
        (attr "transform" (str "translate(" 0 "," height ")"))
        (attr "class" "x-axis")
        (call x-axis))
    (when show-lines? (draw-lines! g data))
    (draw-dots! g data click-handler label-fn)))

(defn beeswarm-plot*
  [{:keys
    [data
     legend-data
     label-fn
     value-fn
     click-handler
     show-lines?
     width
     height]
    :as args}]
  (let [element (atom nil)
        grouped-by-same-x-position (group-by #(int (/ % (* 1000 60 60 24 45)))
                                             (map value-fn data))
        max-grouped (apply max (map count (vals grouped-by-same-x-position)))

        svg-height (+ 240 (* 10 max-grouped))
        destroy!
        (fn [_this]
          (when @element
            (gdom/removeChildren @element)))
        repaint! (fn [this {:keys
                            [data legend-data
                             label-fn value-fn click-handler
                             show-lines? width height]
                            :as args}]
                   (destroy! this)
                   (draw!
                    {:dom-node @element
                     :data data
                     :label-fn label-fn
                     :value-fn value-fn
                     :click-handler click-handler
                     :legend-data legend-data
                     :show-lines? show-lines?}
                    width
                    svg-height))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (repaint! this args))
      :component-will-update
      (fn [this
           [_ args]]
        (repaint! this args))
      :reagent-render
      (fn [_]
        [:div
         {:style
          {:width "100%"
           :height "100%"}}
         [:div
          {:ref #(reset! element %)
           :style
           {:width "100%"
            :height svg-height}}]])})))

(defn beeswarm-plot
  [{:keys [data
           legend-data
           label-fn
           value-fn
           click-handler
           show-lines?] :as args}]
  [:div {:class component-name}
   [(r/adapt-react-class react-sizeme/SizeMe)
    {:monitorWidth true :monitorHeight true}
    (fn [props]
      (let [props (js->clj props :keywordize-keys true)]
        (r/as-element [beeswarm-plot*
                       (assoc args
                              :width (:width (:size props))
                              :height (:height (:size props)))])))]])
